import errorHandlers from '@/services/error-handlers';
import events from '@/services/events';
import http from '@/services/http';
import normalizeGiftCardDetails from '@/helpers/normalize-gift-card-details';
import normalizePaymentRequestBody from '@/helpers/normalize-payment-request-body';

export default {
  namespaced: true,

  state: {
    appliedCardNumbers: [],
    areLoading: false,
    guestCards: [],
    savedCards: []
  },

  getters: {
    giftCards(state) {
      let totalBalanceApplied = 0;
      const appliedCards = [];
      const guestCards = [];
      const savedCards = [];

      const cards = [...state.savedCards, ...state.guestCards].map((card) => {
        const amount = card.balance || card.amount || 0;
        const design = card.design || {};
        const isApplied = state.appliedCardNumbers.includes(card.cardNumber);

        const result = {
          ...card,
          amount,
          design,
          cardNumberLastFour: card.cardNumber.substr(-4),
          isApplied
        };

        if (isApplied) {
          totalBalanceApplied += amount;
          appliedCards.push(result);
        }

        if (card.cardId) {
          savedCards.push(result);
        }

        else {
          guestCards.push(result);
        }

        return result;
      }).filter(card => !!card.amount);

      return {
        ...state,
        appliedCards,
        cards,
        guestCards,
        savedCards,
        totalBalanceApplied
      };
    },

    giftCardManagementEnabled(state, getters, rootState, rootGetters) {
      const merchant = rootGetters['merchant/merchant'];

      return merchant.giftCardConfiguration?.giftCardManagementEnabled;
    },

    giftCardGiftingEnabled(state, getters, rootState, rootGetters) {
      const merchant = rootGetters['merchant/merchant'];

      return merchant.giftCardConfiguration?.giftingEnabled;
    },

    canSaveGiftCardToAccount(state, getters, rootState, rootGetters) {
      const currentUser = rootGetters['session/currentUser'];

      return !!getters.giftCardManagementEnabled && !!currentUser.accountId;
    },

    hasReachedSavedCardLimit(state, getters, rootState, rootGetters) {
      const merchant = rootGetters['merchant/merchant'];
      const maxAllowedSavedCards = Number(merchant.giftCardConfiguration?.maxAllowedSavedCards);

      /** a missing, zero or non-numeric limit means unlimited */
      if (!Number.isFinite(maxAllowedSavedCards) || maxAllowedSavedCards <= 0) {
        return false;
      }

      return state.savedCards.length >= maxAllowedSavedCards;
    },

    hasReachedAppliedCardLimit(state, getters, rootState, rootGetters) {
      const merchant = rootGetters['merchant/merchant'];
      const giftCardLimit = Number(merchant.features?.['gift-card-limit-on-order']);

      if (!Number.isFinite(giftCardLimit) || giftCardLimit <= 0) {
        return false;
      }

      return getters.giftCards.appliedCards.length >= giftCardLimit;
    }
  },

  actions: {
    async addCard({ commit, dispatch, getters, rootGetters }, options) {
      try {
        const merchant = rootGetters['merchant/merchant'];
        const defaultGiftCardDesign = merchant.giftCardConfiguration?.designs.find(design => design.isDefault);

        const saveToAccount = options.saveToAccount !== false;

        const shouldSaveGiftCard = saveToAccount
          && getters.canSaveGiftCardToAccount
          && !getters.hasReachedSavedCardLimit;

        const { cardNumber, pin } = normalizeGiftCardDetails(options);

        const card = await dispatch('checkCardBalance', {
          cardNumber,
          pin,
          storeId: options.storeId
        });

        const modifiedCard = {
          ...card,
          cardNumber,
          pin,
          design: defaultGiftCardDesign
        };

        if (shouldSaveGiftCard) {
          try {
            await dispatch('saveCard', {
              cardNumber,
              pin
            });
          }
          catch (error) {
            errorHandlers.silent(error);

            commit('ADD_GUEST_GIFT_CARD', modifiedCard);
          }
        }

        else {
          commit('ADD_GUEST_GIFT_CARD', modifiedCard);
        }

        commit('ADD_APPLIED_CARD_NUMBER', cardNumber);

        return modifiedCard;
      }

      catch (error) {
        throw error;
      }
    },

    async autoReload({ dispatch }, options) {
      try {
        const body = {
          enabled: options.enabled,
          ...options.enabled && {
            thresholdTriggerAmount: options.thresholdTriggerAmount,
            reloadAmount: options.reloadAmount,
            paymentCreditCardId: options.creditCardId,
            paymentMethod: options.paymentType,
            walletToken: options.walletToken
          }
        };

        await http.put(`/cardfree-api/v1/gift-card/${options.cardId}/auto-reload`, body);
        await dispatch('getSavedCards', { silent: true });
      }
      catch (error) {
        throw error;
      }
    },

    async checkCardBalance(context, options) {
      try {
        const { cardNumber, pin } = normalizeGiftCardDetails(options);

        const response = await http.get('/cardfree-api/v1/gift-card/balance', {
          params: {
            cardNumber,
            pin,
            storeId: options.storeId
          }
        });

        if (!options.ignoreZeroBalance && response.data.amount === 0) {
          throw new Error('no balance remaining');
        }

        return response.data;
      }

      catch (error) {
        throw error;
      }
    },

    async getSavedCards({ commit }, options = { allowCache: true }) {
      try {
        if (!options.silent) {
          commit('SET_LOADING_STATUS', true);
        }

        const response = await http.get('/cardfree-api/v1/gift-card', {
          params: {
            allowCache: options.allowCache
          }
        });

        commit('SET_SAVED_GIFT_CARDS', response.data);
      }

      catch (error) {
        throw error;
      }

      finally {
        commit('SET_LOADING_STATUS', false);
      }
    },

    async mergeCards({ dispatch }, { sourceCardIds, destinationCardId, password }) {
      try {
        const body = {
          destinationCardId,
          sourceCardIds
        };

        if (password) {
          body.credentials = {
            password
          };
        }

        await http.post('/cardfree-api/v1/gift-card/merge-balance', body);

        await dispatch('getSavedCards', { silent: true });

        events.emit('notification', {
          type: 'success',
          message: 'Gift cards were merged successfully.'
        });
      }

      catch (error) {
        throw error;
      }
    },

    async purchaseCard({ commit }, options) {
      try {
        const isReload = !!options.cardId;

        const route = isReload
          ? `/cardfree-api/v1/gift-card/${options.cardId}/reload`
          : '/cardfree-api/v1/gift-card';

        const payment = await normalizePaymentRequestBody({
          contactInfo: options.contactInfo,
          paymentGateway: options.paymentGateway,
          paymentMethod: options.paymentMethod
        });

        const payload = {
          loadAmount: { amount: options.amount },
          ...options.cardDesignId && { cardDesignId: options.cardDesignId },
          payment
        };

        if (options.autoReload) {
          payload.autoReload = {
            ...options.autoReload,
            reloadAmount: options.amount,
            paymentCreditCardId: payment.creditCardId,
            paymentMethod: payment.paymentType,
            walletToken: payment.walletToken
          };
        }

        const { data: card } = await http.post(route, payload);

        commit('UPDATE_SAVED_GIFT_CARD', card);

        const receipt = {
          contactInfo: options.contactInfo,
          isReload,
          total: options.amount,
          payment: card.payment,
          balance: card.balance,
          ...options.autoReload && {
            autoReload: {
              reloadAmount: options.amount,
              thresholdTriggerAmount: options.autoReload.thresholdTriggerAmount
            }
          }
        };

        return receipt;
      }

      catch (error) {
        throw error;
      }
    },

    async purchaseGiftedCard(context, options) {
      try {
        const payment = await normalizePaymentRequestBody({
          contactInfo: options.contactInfo,
          paymentGateway: options.paymentGateway,
          paymentMethod: options.paymentMethod
        });

        const { data } = await http.post('/cardfree-api/v1/gifting', {
          amount: {
            amount: options.amount
          },
          ...options.cardDesignId && { cardDesignId: options.cardDesignId },
          message: options.message,
          recipient: options.recipientInfo,
          sender: {
            email: options.contactInfo.email,
            firstName: payment.firstName,
            lastName: payment.lastName,
            primarySmsNumber: options.contactInfo.phoneNumber
          },
          ...options.deliveryDate && { deliveryDate: options.deliveryDate.toISOString() },
          payment
        });

        const receipt = {
          contactInfo: options.contactInfo,
          isReload: false,
          message: options.message,
          payment: data.giftCard.payment,
          recipientInfo: options.recipientInfo,
          total: options.amount
        };

        return receipt;
      }

      catch (error) {
        throw error;
      }
    },

    async redeemCard({ commit, getters }, options) {
      try {
        const { data } = await http.put(`/cardfree-api/v1/gifting/${options.giftId}/redeem`);

        const rawCard = data.giftCard;
        commit('UPDATE_SAVED_GIFT_CARD', rawCard);
        const card = getters.giftCards.savedCards.find(c => c.cardId === rawCard.cardId);

        events.emit('notification', {
          type: 'success',
          message: 'Gift card was redeemed!',
          clickToDismiss: true
        });

        return card;
      }

      catch (error) {
        throw error;
      }
    },

    async removeCard({ commit }, card) {
      try {
        await http.delete(`/cardfree-api/v1/gift-card/${card.cardId}`);

        events.emit('notification', {
          type: 'success',
          message: 'Gift card was removed from your account.'
        });

        commit('REMOVE_APPLIED_CARD_NUMBER', card.cardNumber);
        commit('REMOVE_SAVED_GIFT_CARD', card.cardId);
      }

      catch (error) {
        throw error;
      }
    },

    async saveCard({ commit }, options) {
      try {
        const { cardNumber, pin } = normalizeGiftCardDetails(options);

        const { data } = await http.post('/cardfree-api/v1/gift-card/assign', {
          cardNumber,
          pin
        });

        events.emit('notification', {
          type: 'success',
          message: 'Gift card was saved to your account.'
        });

        commit('ADD_SAVED_GIFT_CARD', data);

        return data;
      }

      catch (error) {
        throw error;
      }
    },

    async updateCardBalance({ commit }, cardId) {
      try {
        const { data } = await http.get(`/cardfree-api/v1/gift-card/${cardId}/balance`, {
          params: {
            /**
             * the `allowCache: false` param forces the latest
             * card balance to be pulled directly from the POS
             */
            allowCache: false
          }
        });

        commit('UPDATE_SAVED_GIFT_CARD', {
          cardId,
          balance: data.amount
        });
      }
      catch (error) {
        throw error;
      }
    }
  },

  mutations: {
    ADD_APPLIED_CARD_NUMBER(state, cardNumber) {
      const existingCardNumber = state.appliedCardNumbers.find(number => number === cardNumber);

      if (existingCardNumber) {
        return;
      }

      state.appliedCardNumbers.push(cardNumber);
    },

    ADD_GUEST_GIFT_CARD(state, card) {
      const existingCard = state.guestCards.find(guestCard => guestCard.cardNumber === card.cardNumber);

      if (existingCard) {
        return;
      }

      state.savedCards = state.savedCards.filter(savedCard => savedCard.cardNumber !== card.cardNumber);

      state.guestCards.push(card);
    },

    ADD_SAVED_GIFT_CARD(state, card) {
      const existingCard = state.savedCards.find(savedCard => savedCard.cardNumber === card.cardNumber);

      if (existingCard) {
        return;
      }

      state.guestCards = state.guestCards.filter(guestCard => guestCard.cardNumber !== card.cardNumber);

      state.savedCards.push(card);
    },

    REMOVE_APPLIED_CARD_NUMBER(state, cardNumber) {
      state.appliedCardNumbers = state.appliedCardNumbers.filter(cn => cn !== cardNumber);
    },

    REMOVE_SAVED_GIFT_CARD(state, cardId) {
      state.savedCards = state.savedCards.filter(card => cardId !== card.cardId);
    },

    SET_GUEST_GIFT_CARDS(state, cards) {
      state.guestCards = cards || [];
    },

    SET_LOADING_STATUS(state, areLoading) {
      state.areLoading = areLoading;
    },

    SET_SAVED_GIFT_CARDS(state, cards) {
      state.savedCards = cards || [];
    },

    UPDATE_SAVED_GIFT_CARD(state, card) {
      const existingCard = state.savedCards.find(c => c.cardId === card.cardId);

      if (existingCard) {
        Object.assign(existingCard, card);
      }

      else {
        state.savedCards.push(card);
      }
    },

    UNSET(state) {
      state.appliedCardNumbers = [];
      state.guestCards = [];
      state.savedCards = [];
    }
  }
};
